import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  labClinicsService,
  labPatientsService,
  labProtocolsService,
  labResultsService,
  labReferenceValuesService,
  labSpeciesService,
  LabClinicCreate,
  LabClinicUpdate,
  LabClinicFilters,
  LabPatientCreate,
  LabPatientUpdate,
  LabPatientFilters,
  LabProtocolCreate,
  LabProtocolUpdate,
  LabProtocolFilters,
  LabResultCreate,
  LabResultUpdate,
  LabReferenceValueCreate,
  LabReferenceValueUpdate,
  LabReferenceValueFilters,
  LabSpeciesFilters,
  LabSpeciesCreate,
  LabSpeciesUpdate,
} from '@/services/lab';
import { LabProtocolStatus } from '@/types';

// ==================== Lab Clinics Hooks ====================

export function useLabClinics(filters?: LabClinicFilters) {
  return useQuery({
    queryKey: ['lab-clinics', filters],
    queryFn: () => labClinicsService.getClinics(filters),
  });
}

export function useLabClinic(id: number | null) {
  return useQuery({
    queryKey: ['lab-clinic', id],
    queryFn: () => labClinicsService.getClinic(id!),
    enabled: !!id,
  });
}

export function useCreateLabClinic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LabClinicCreate) => labClinicsService.createClinic(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-clinics'] });
    },
  });
}

export function useUpdateLabClinic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: LabClinicUpdate }) =>
      labClinicsService.updateClinic(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lab-clinics'] });
      queryClient.invalidateQueries({ queryKey: ['lab-clinic', variables.id] });
    },
  });
}

export function useDeleteLabClinic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => labClinicsService.deleteClinic(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-clinics'] });
    },
  });
}

// ==================== Lab Patients Hooks ====================

export function useLabPatients(filters?: LabPatientFilters) {
  return useQuery({
    queryKey: ['lab-patients', filters],
    queryFn: () => labPatientsService.getPatients(filters),
  });
}

export function useLabPatient(id: number | null) {
  return useQuery({
    queryKey: ['lab-patient', id],
    queryFn: () => labPatientsService.getPatient(id!),
    enabled: !!id,
  });
}

export function useCreateLabPatient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LabPatientCreate) => labPatientsService.createPatient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-patients'] });
    },
  });
}

export function useUpdateLabPatient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: LabPatientUpdate }) =>
      labPatientsService.updatePatient(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lab-patients'] });
      queryClient.invalidateQueries({ queryKey: ['lab-patient', variables.id] });
    },
  });
}

export function useDeleteLabPatient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => labPatientsService.deletePatient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-patients'] });
    },
  });
}

// ==================== Lab Protocols Hooks ====================

export function useLabProtocols(filters?: LabProtocolFilters) {
  return useQuery({
    queryKey: ['lab-protocols', filters],
    queryFn: () => labProtocolsService.getProtocols(filters),
  });
}

export function useLabProtocol(id: number | null) {
  return useQuery({
    queryKey: ['lab-protocol', id],
    queryFn: () => labProtocolsService.getProtocol(id!),
    enabled: !!id,
  });
}

export function useCreateLabProtocol() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LabProtocolCreate) => labProtocolsService.createProtocol(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-protocols'] });
    },
  });
}

export function useCreateLabProtocolWithResults() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LabProtocolCreate & { results: LabResultCreate[] }) =>
      labProtocolsService.createProtocolWithResults(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-protocols'] });
    },
  });
}

export function useUpdateLabProtocol() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: LabProtocolUpdate }) =>
      labProtocolsService.updateProtocol(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lab-protocols'] });
      queryClient.invalidateQueries({ queryKey: ['lab-protocol', variables.id] });
    },
  });
}

export function useUpdateLabProtocolStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: LabProtocolStatus }) =>
      labProtocolsService.updateProtocolStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lab-protocols'] });
      queryClient.invalidateQueries({ queryKey: ['lab-protocol', variables.id] });
    },
  });
}

export function useDeleteLabProtocol() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => labProtocolsService.deleteProtocol(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-protocols'] });
    },
  });
}

// ==================== Lab Results Hooks ====================

export function useLabResults(protocolId: number | null) {
  return useQuery({
    queryKey: ['lab-results', protocolId],
    queryFn: () => labResultsService.getResults(protocolId!),
    enabled: !!protocolId,
  });
}

export function useCreateLabResult() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ protocolId, data }: { protocolId: number; data: LabResultCreate }) =>
      labResultsService.createResult(protocolId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lab-results', variables.protocolId] });
      queryClient.invalidateQueries({ queryKey: ['lab-protocol', variables.protocolId] });
    },
  });
}

export function useCreateLabResultsBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ protocolId, results }: { protocolId: number; results: LabResultCreate[] }) =>
      labResultsService.createResultsBulk(protocolId, results),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lab-results', variables.protocolId] });
      queryClient.invalidateQueries({ queryKey: ['lab-protocol', variables.protocolId] });
    },
  });
}

export function useUpdateLabResult() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: LabResultUpdate; protocolId: number }) =>
      labResultsService.updateResult(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lab-results', variables.protocolId] });
      queryClient.invalidateQueries({ queryKey: ['lab-protocol', variables.protocolId] });
    },
  });
}

export function useDeleteLabResult() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: number; protocolId: number }) =>
      labResultsService.deleteResult(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lab-results', variables.protocolId] });
      queryClient.invalidateQueries({ queryKey: ['lab-protocol', variables.protocolId] });
    },
  });
}

// ==================== Lab Reference Values Hooks ====================

export function useLabReferenceValues(filters?: LabReferenceValueFilters) {
  return useQuery({
    queryKey: ['lab-reference-values', filters],
    queryFn: () => labReferenceValuesService.getReferenceValues(filters),
  });
}

export function useCreateLabReferenceValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LabReferenceValueCreate) =>
      labReferenceValuesService.createReferenceValue(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-reference-values'] });
    },
  });
}

export function useUpdateLabReferenceValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: LabReferenceValueUpdate }) =>
      labReferenceValuesService.updateReferenceValue(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-reference-values'] });
    },
  });
}

export function useDeleteLabReferenceValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => labReferenceValuesService.deleteReferenceValue(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-reference-values'] });
    },
  });
}

export function useSeedCanineReferenceValues() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => labReferenceValuesService.seedCanineValues(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-reference-values'] });
    },
  });
}

// ==================== Lab Species ====================
export function useLabSpecies(filters?: LabSpeciesFilters) {
  return useQuery({
    queryKey: ['lab-species', filters],
    queryFn: () => labSpeciesService.getAll(filters),
  });
}

export function useCreateLabSpecies() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: LabSpeciesCreate) => labSpeciesService.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['lab-species'] }); },
  });
}

export function useUpdateLabSpecies() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: LabSpeciesUpdate }) => labSpeciesService.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['lab-species'] }); },
  });
}

export function useDeleteLabSpecies() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => labSpeciesService.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['lab-species'] }); },
  });
}
